import type { PotmTileProps } from '@modules/projects-category/components/directory-grid-potm/DirectoryPotmTileDesktop';
import {
    chunkItems,
    injectItemIntoChunk,
} from '@modules/projects-category/util/inject-directory-items';
import type { AdvertCreative, ProjectAd } from '~/modules/adverts/type';
import type { ProjectTileViewModel as AdvertCreativeProjectTileViewModel } from '@modules/products-category/util/advert-creative-project-tile-view';
import type { PaginationState } from '~/modules/pagination/type';
import type { ProjectTileViewModel } from '~/modules/search/util/elasticsearch-project-tile-view';

const POTMS_PER_PAGE = 3;
const TILES_CHUNK_SIZE = 12;
const TILES_CHUNK_SIZE_WITH_POTMS = 10;

interface WithKey {
    key: string;
}

/**
 * Information to render a regular Project tile
 */
export interface ProjectImpressionGridTile extends WithKey {
    component: 'ProjectImpressionTile';
    data: {
        project: ProjectTileViewModel;
        index: number;
    };
}

/**
 * Information to render a sponsored Project tile. This looks like a regular
 * Project tile, but it displays a "sponsored" project.
 */
export interface ProjectAdvertGridTile extends WithKey {
    component: 'ProjectAdvertTile';
    data: {
        project: AdvertCreativeProjectTileViewModel;
        index: number;
    };
}

/**
 * Information to render a banner advert. The banner breaks up the project grid
 * span the full width of the grid and clearly looks like an advertisement.
 */
export interface AdvertProjectPlacementGridTile extends WithKey {
    component: 'AdvertProjectPlacementTile';
    data: {
        ad: ProjectAd;
    };
}

/**
 * Information to render a Project of the Month vertical tile. This tile is
 * twice the height of a regular project tile and can contain a video.
 */
export interface DirectoryPotmDesktopGridTile extends WithKey {
    component: 'DirectoryPotmTileDesktop';
    data: {
        potmData: PotmTileProps;
    };
}

/**
 * Displays a vertical tile advertisement. It looks like a Project vertical
 * tile.
 */
export interface AdvertPotmDesktopGridTile extends WithKey {
    component: 'AdvertPotmTileDesktop';
    data: {
        advertCreative: AdvertCreative;
    };
}

/**
 * Information to render a big banner advert at the top of the page
 */
export interface AdvertBannerGridTile extends WithKey {
    component: 'AdvertBannerTile';
    data: {
        advertCreative: AdvertCreative;
    };
}

/**
 * Information to render a banner to advertise AP Connect events.
 *
 * Not sure what that actually looks like
 */
interface APConnectBannerGridTile extends WithKey {
    component: 'APConnectBanner';
    data: {};
}

/**
 * Unions of all the possible items that can be rendered via a "Project of
 * the Month" grid.
 */
export type GridTile =
    | ProjectImpressionGridTile
    | ProjectAdvertGridTile
    | AdvertProjectPlacementGridTile
    | DirectoryPotmDesktopGridTile
    | AdvertBannerGridTile
    | APConnectBannerGridTile
    | AdvertPotmDesktopGridTile;

/**
 * Type guard method to filter out all non-values out of an array
 */
function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
    return value !== null && value !== undefined;
}

/**
 * Assembles a grid of project tiles, advertisements, and Project of the Month (POTM) tiles.
 * The grid is organized in chunks with specific positioning for ads and POTMs.
 *
 * We don't actually render the grid tiles here. We just assemble them in an array that can
 * be looped through by the rendering method.
 *
 * The Grid is 3 tiles wide on desktop and 1 tile wide on mobile.
 *
 * On desktop, it normally looks like this:
 * - 1 row of the 3 sponsored project tiles (omitted if no adverts are present)
 * - 1 rows of 3 tiles
 * - 1 banner advertisement
 * - 2 rows of tiles with the two right tiles merged into a vertical tile
 * - 2 rows of tiles
 * - 1 banner advertisement
 * - 2 rows of tiles with the two left tiles merged into a vertical tile
 * - 2 rows of tiles
 * - 1 banner advertisement
 * - 2 rows of tiles with the two right tiles merged into a vertical tile
 * - 2 rows of tiles
 *
 * The vertical tiles will display  "Projets of the Month" (POTMN). They can be
 * swapped with sponsored Vertical placement tiles.
 *
 * This looks like:
 * ┌─┐┌─┐┌─┐
 * └─┘└─┘└─┘
 * ┌─┐┌─┐┌─┐
 * └─┘└─┘└─┘
 * ┌────────┐
 * │ banner │
 * └────────┘
 * ┌─┐┌─┐┌─┐
 * └─┘└─┘│ │
 * ┌─┐┌─┐│ │
 * └─┘└─┘└─┘
 * ┌─┐┌─┐┌─┐
 * └─┘└─┘└─┘
 * ┌─┐┌─┐┌─┐
 * └─┘└─┘└─┘
 * ┌────────┐
 * │ banner │
 * └────────┘
 * ┌─┐┌─┐┌─┐
 * │ │└─┘└─┘
 * │ │┌─┐┌─┐
 * └─┘└─┘└─┘
 * ┌─┐┌��┐┌─┐
 * └─┘└─┘└─┘
 * ┌─┐┌─┐┌─┐
 * └─┘└─┘└─┘
 * ┌────────┐
 * │ banner │
 * └────────┘
 * ┌─┐┌─┐┌─┐
 * └─┘└─┘│ │
 * ┌─┐┌─┐│ │
 * └─┘└─┘└─┘
 * ┌─┐┌─┐┌─┐
 * └─┘└─┘└─┘
 * ┌─┐┌─┐┌─┐
 * └─┘└─┘└─┘
 *
 * @param showArchiproConnectBanner - Whether to show the Archipro Connect banner at the top of the grid
 * @param desktop - Whether viewing on desktop
 * @param ads - Full-width advertisement creatives displayed at the top of the page
 * @param verticalTileAds - Vertical tile advertisements that replace the regular vertical tile projects.
 * @param sponsoredAds - Sponsored project advertisements displayed at the top of the grid. They look like
 * regular project tiles, but with the words "sponsored".
 * @param projectAds - Banner ads that will break up the grid
 * @param potmFeatureOn - Whether to display vertical tile projects of the month.
 * @param projectTiles - Regular project tiles to display
 * @param gridPotms - Project of the Month tiles that will be showed in vertical tiles.
 * @param appliedFilterCount - Number of active filters. When filters are applied, some adverts get suppressed.
 * @returns Array of grid tiles in display order
 */
export const buildProjectTileGrid = (
    showArchiproConnectBanner: boolean,
    desktop: boolean,
    ads: AdvertCreative[],
    verticalTileAds: AdvertCreative[],
    sponsoredAds: AdvertCreativeProjectTileViewModel[],
    projectAds: ProjectAd[],
    potmFeatureOn: boolean,
    projectTiles: ProjectTileViewModel[],
    gridPotms: PotmTileProps[],
    appliedFilterCount: number,
    pagination: PaginationState
): GridTile[] => {
    // First, we build the baseline array of project tiles
    const tiles: GridTile[] = projectTiles.map((project, index) => {
        return {
            component: 'ProjectImpressionTile',
            data: {
                project,
                index,
            },
            key: project.tile.projectLink,
        };
    });

    // If our project list is unfiltered and we have sponsored projects, we prepend
    // the sponsored project at the top of the list
    if (appliedFilterCount === 0 && sponsoredAds.length > 0) {
        sponsoredAds.forEach((project, index) => {
            if (!project.advertCreative) {
                return;
            }

            tiles.unshift({
                component: 'ProjectAdvertTile',
                data: {
                    project,
                    index,
                },
                key: `sponsored_${project.tile.projectLink}`,
            });
        });
    }

    // Create separate arrays for POTMs and vertical ad tiles, but only on desktop.
    // These will be combined later to create the vertical tiles that appear in the grid.
    // gridHeroTiles contains regular POTMs
    // gridAdvertTiles contains sponsored vertical tile ads
    const gridHeroTiles: GridTile[] = [];
    const gridAdvertTiles: GridTile[] = [];
    if (desktop) {
        verticalTileAds.forEach((advert) => {
            gridAdvertTiles.push({
                component: 'AdvertPotmTileDesktop',
                data: { advertCreative: advert },
                key: `advert-${advert.id}`,
            });
        });
        gridPotms.forEach((gridPtom) => {
            gridHeroTiles.push({
                component: 'DirectoryPotmTileDesktop',
                data: { potmData: gridPtom },
                key: `potm-${gridPtom.id}`,
            });
        });
    }

    // We only show the Project of the month vertical tiles if
    // - the POTM feature is enabled
    // - we are on desktop
    // - there are more than 2 POTMs
    const gridPotmsEnabled =
        potmFeatureOn &&
        desktop &&
        gridHeroTiles.length > 2 &&
        (appliedFilterCount === 0 || verticalTileAds.length > 0);

    let preparedGridPotms: GridTile[] = [];
    let paginatedPotms: GridTile[] = [];
    let verticalTileAdsCount = 0;
    if (gridAdvertTiles[0]) {
        paginatedPotms.push(gridAdvertTiles[0]);
        verticalTileAdsCount = 1;
    }
    if (gridPotmsEnabled) {
        // Calculate POTMs per page, accounting for vertical ad tiles
        // If we have a vertical ad, we show 2 POTMs instead of 3
        const verticalTilesPage = POTMS_PER_PAGE - verticalTileAdsCount;

        // Calculate starting position for POTMs on current page
        // e.g., page 1: offset = 0, page 2: offset = 2, page 3: offset = 4
        const potmOffset = (pagination.page - 1) * verticalTilesPage;

        // Determine if we should show POTMs based on whether we've reached the end
        // Add 1 to account for zero-based indexing
        const showPotms =
            pagination.page * verticalTilesPage <= gridHeroTiles.length + 1;

        // Calculate max number of vertical tiles (POTMs + ads) we can fit on this page
        // Based on available regular tiles to fill surrounding spaces
        // Subtract verticalTileAdsCount to reserve space for ad tiles
        const potmLimit =
            Math.floor(tiles.length / TILES_CHUNK_SIZE_WITH_POTMS) -
            verticalTileAdsCount;

        let potmsToShow = gridHeroTiles.slice(
            potmOffset,
            potmOffset + potmLimit
        );

        // Get POTMs for current page, limited by potmLimit
        paginatedPotms = paginatedPotms.concat(
            gridHeroTiles.slice(potmOffset, potmOffset + potmLimit)
        );

        // Calculate how many additional POTMs we need to fill the remaining slots
        // We only add more POTMs if all these conditions are met:
        // 1. showPotms is true (we haven't reached the end of available POTMs)
        // 2. The remaining space (potmLimit - potmsToShow.length) is small enough
        //    that filling it won't exceed POTMS_PER_PAGE when combined with ads
        // 3. There's actually space to fill (potmLimit > potmsToShow.length)
        const missingPotms = !showPotms
            ? 0
            : potmLimit - potmsToShow.length >
                POTMS_PER_PAGE - verticalTileAdsCount - 1
              ? 0
              : potmLimit - potmsToShow.length;

        preparedGridPotms =
            paginatedPotms.length > 0 &&
            missingPotms > 0 &&
            missingPotms < POTMS_PER_PAGE
                ? [...paginatedPotms, ...gridHeroTiles.slice(0, missingPotms)]
                : paginatedPotms;
    } else {
        // If POTMs are disabled, just use any vertical ad tiles
        preparedGridPotms = paginatedPotms;
    }

    // Split tiles into chunks. For chunks that will contain a POTM/vertical ad,
    // we use TILES_CHUNK_SIZE_WITH_POTMS (10 tiles). For remaining chunks without
    // vertical tiles, we use TILES_CHUNK_SIZE (12 tiles). Each chunk will get a
    // banner advert injected later.
    let tileChunks: GridTile[][] = [];
    let offset = 0;
    preparedGridPotms.forEach((_, index) => {
        offset = index * TILES_CHUNK_SIZE_WITH_POTMS;
        tileChunks = tileChunks.concat(
            chunkItems(
                tiles.slice(offset, offset + TILES_CHUNK_SIZE_WITH_POTMS),
                TILES_CHUNK_SIZE_WITH_POTMS
            )
        );
        offset += TILES_CHUNK_SIZE_WITH_POTMS;
    });
    tileChunks = tileChunks.concat(
        chunkItems(tiles.slice(offset), TILES_CHUNK_SIZE)
    );

    // Let's loop through each chunk and inject our banner and vertical tile (if any)
    const injectedTileChunks = tileChunks.map((chunk, index) => {
        // Prepare the banner advert. The banner advert is displayed after the
        // first two rows of tiles (so 6 tiles in total).
        const adPosition = 6;
        const adData = projectAds[index];
        const ad: AdvertProjectPlacementGridTile | null = adData
            ? {
                  component: 'AdvertProjectPlacementTile',
                  data: {
                      ad: adData,
                  },
                  key: `project-placement-${adData.id}`,
              }
            : null;

        // Prepare our vertical tile. The vertical tile alternate from right to
        // left in each chunk, so they will be injected at different position
        // based on whether we are working on an even or odd chunk.
        const potmPosition = index % 2 === 0 ? 8 : 6;

        // Find the vertical tile for this chunk
        const potm: GridTile | null = preparedGridPotms[index] || null;

        // We inject our vertical tile and banner advert into the chunk.
        return injectItemIntoChunk(
            chunk,
            [potm, ad],
            [potmPosition, adPosition]
        );
    });

    // Let's take our chunk list and flatten it back into a single list.
    const flattenedTiles = injectedTileChunks
        .reduce((flatArray, currentArray) => flatArray.concat(currentArray), [])
        .filter(notEmpty);

    // Prepend a big full width banner advert to the list if we have one.
    if (appliedFilterCount === 0 && ads.length && ads[0]) {
        flattenedTiles.unshift({
            component: 'AdvertBannerTile',
            data: {
                advertCreative: ads[0],
            },
            key: 'AdvertBannerTile',
        });
    }

    // Prepend an AP Connect banner advert to the list if needed
    if (showArchiproConnectBanner) {
        flattenedTiles.unshift({
            component: 'APConnectBanner',
            data: {},
            key: 'APConnectBanner',
        });
    }

    return flattenedTiles;
};
